import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  listNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '../API/notificationApi';
import { getActiveToken } from '../utils/jwt';
import useAuth from '../hooks/useAuth';

const NotificationsContext = createContext({
  notifications: [],
  unreadCount: 0,
  loading: false,
  refresh: () => {},
  markRead: async () => {},
  markAllRead: async () => {},
});

const POLL_INTERVAL_MS = 60000;

const NotificationsProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const socketRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const [list, counts] = await Promise.all([
        listNotifications({ limit: 50 }).catch(() => ({ data: [] })),
        getUnreadCount().catch(() => ({ unread: 0 })),
      ]);
      setNotifications(Array.isArray(list?.data) ? list.data : []);
      setUnreadCount(counts?.unread || 0);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      return undefined;
    }
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isAuthenticated, refresh]);

  useEffect(() => {
    if (!isAuthenticated || !user) return undefined;
    const token = getActiveToken();
    if (!token) return undefined;

    let active = true;
    let socket = null;

    (async () => {
      try {
        const ioModule = await import('socket.io-client');
        if (!active) return;
        const io = ioModule.io || ioModule.default;
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        socket = io(baseUrl, {
          auth: { token },
          transports: ['websocket', 'polling'],
          reconnection: true,
        });

        socket.on('notification', (notif) => {
          setNotifications((prev) => [notif, ...prev].slice(0, 100));
          if (!notif.is_read) setUnreadCount((c) => c + 1);
        });

        socket.on('connect_error', () => {});
        socketRef.current = socket;
      } catch (err) {
        // socket.io-client not yet installed (run `npm install`); polling continues.
      }
    })();

    return () => {
      active = false;
      if (socket) {
        socket.removeAllListeners();
        socket.disconnect();
      }
      socketRef.current = null;
    };
  }, [isAuthenticated, user]);

  const markRead = useCallback(async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      // ignore — backend will reject with 404 if not owned, fine
    }
  }, []);

  const markAllRead = useCallback(async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, is_read: true, read_at: new Date().toISOString() })),
      );
      setUnreadCount(0);
    } catch (err) {
      // ignore
    }
  }, []);

  const value = useMemo(
    () => ({ notifications, unreadCount, loading, refresh, markRead, markAllRead }),
    [notifications, unreadCount, loading, refresh, markRead, markAllRead],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export { NotificationsContext, NotificationsProvider };
